/**
 * @file NoSQL database implementation layer
 * @copyright Generic code designed and created by Kyley Harris
 * @license This is generic utility code not unique to the AGWA project.
 * Created and maintained by Kyley Harris with usage rights granted to all projects.
 *
 * This module implements a simple key-value NoSQL database using IndexedDB.
 * Key features:
 * - Type-safe data storage and retrieval with generics
 * - Automatic data expiration based on time-to-live
 * - User-specific data isolation
 * - URL-based data fetching and caching
 * - Singleton pattern for global access
 */

// this represents the object layer of the data layer
import { emptyGuid } from './guid';
import * as db from './indexed-db';

import { IndexedDBAccess as IndexedDbAccess } from './indexed-db-wrapper';
import { INoSqlDb, JsonData } from './no-sql-db-intf';

/** Type alias for string-based unique identifiers */
export type guid = string;

/**
 * Object store names used in the database.
 * Using a const object ensures consistency across the codebase.
 */
export const stores = {
  all: 'all' as const,
};

/**
 * Database initialization function for version 1.
 * Creates the main object store that will hold all data items.
 *
 * @param idb - The database instance being upgraded
 */
function createVersion1(idb: IDBDatabase) {
  db.newDbItemObjectStore(idb, {
    name: stores.all,
  });
}

/** Special value: expire at end of day (next day invalid) */
export const nextDayExpiry = -100;

/**
 * Helper Functions
 */

/**
 * Converts a string or Date object to a Date.
 * Ensures consistent date handling regardless of input format.
 *
 * @param d - Date value (either string or Date object)
 * @returns Normalized Date object
 */
function toDate(d: Date | string): Date {
  return d instanceof Date ? d : new Date(d);
}

/**
 * Determines if two dates fall on the same calendar day.
 * Used for handling nextDayExpiry logic.
 *
 * @param d1 - First date to compare
 * @param d2 - Second date to compare
 * @returns True if both dates are on the same day
 */
function sameDay(d1: Date, d2: Date): boolean {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/**
 * Core NoSQL database implementation class.
 *
 * This class extends IndexedDBAccess to leverage the underlying IndexedDB
 * functionality while providing a higher-level NoSQL-style interface.
 * It implements the INoSqlDb interface to provide:
 *
 * - Simple key-value storage with automatic expiration
 * - User-scoped data isolation
 * - URL-based data fetching and caching
 * - Type-safe data retrieval with generics
 */
export class NoSqlDb extends IndexedDbAccess implements INoSqlDb {
  /** Reference to the last fetched data item for debugging/status */
  public lastFetch: JsonData | null = null;

  /** Name of the IndexedDB database */
  private _dbName = 'wm-no-sql';

  /**
   * Returns the database name used by this instance.
   * Required implementation from IndexedDBAccess.
   *
   * @returns The name of the IndexedDB database
   */
  public dbName(): string {
    return this._dbName;
  }

  /**
   * Clears all data from the database.
   * This is a destructive operation that removes all stored items.
   *
   * @returns Promise that resolves when the database is cleared
   */
  public async clear(): Promise<void> {
    const conn = await this.connect();
    await db.deleteAll(conn, stores.all);
    this.delayedClose();
  }

  /**
   * Checks if a JsonData item has expired based on its created and expires properties.
   *
   * @param jsonData - The data item to check for expiration
   * @returns True if the item has expired, false otherwise
   */
  jsonDataExpired(jsonData: JsonData): boolean {
    return this.expired(jsonData.created, jsonData.expires);
  }

  /**
   * Internal method to retrieve a JsonData item by key with user isolation.
   * This is a lower-level method that returns the full JsonData wrapper.
   *
   * @param key - The key to retrieve
   * @param userId - Optional user ID for data isolation
   * @param allowExpired - If true, returns expired items instead of null
   * @returns Promise resolving to the JsonData item or null
   */
  async getJsonData(
    key: string,
    userId?: string,
    allowExpired: boolean = false
  ): Promise<JsonData | null> {
    const idxKey = this.getId(key, userId ?? emptyGuid);
    const jsonData = await this.getFirst<JsonData>(stores.all, {
      query: idxKey,
    });
    if (!jsonData) return null;

    // Coerce any string dates coming back from IDB
    const created = toDate(jsonData.created);
    const expires = jsonData.expires ?? 0;

    const expired = this.expired(created, expires);
    if (expired) {
      await this.del(idxKey, stores.all);
      return allowExpired ? { ...jsonData, created, expires } : null;
    }
    return { ...jsonData, created, expires };
  }

  /**
   * Retrieves a typed item by key with optional user isolation.
   * This is the main method for data retrieval.
   *
   * @template T - The expected type of the stored value
   * @param key - The key to retrieve
   * @param userId - Optional user ID for data isolation
   * @returns Promise resolving to the typed value or null
   */
  async getItem<T>(key: string, userId?: string): Promise<T | null> {
    const jsonData = await this.getJsonData(key, userId);
    return (jsonData?.data as T) ?? null;
  }

  /**
   * Deletes an item by key with optional user isolation.
   *
   * @param key - The key to delete
   * @param userId - Optional user ID for data isolation
   * @returns Promise that resolves when deletion is complete
   */
  async delItem(key: string, userId?: string): Promise<void> {
    const idxKey = this.getId(key, userId ?? emptyGuid);
    await this.del(idxKey, stores.all);
  }

  /**
   * Deletes a grabbed (cached) item by key with optional user isolation.
   * This is used for clearing fetch/grab caches.
   *
   * @param key - The base key (without the grab:// prefix)
   * @param userId - Optional user ID for data isolation
   * @returns Promise that resolves when deletion is complete
   */
  async delGrabItem(key: string, userId?: string): Promise<void> {
    const grabKey = `grab://${key}`;
    const idxKey = this.getId(grabKey, userId ?? emptyGuid);
    await this.del(idxKey, stores.all);
  }

  /**
   * Stores a value with the specified key and expiration.
   * Uses default expiration of 1 week (604800 seconds) if not specified.
   *
   * @template T - The type of value being stored
   * @param key - The key to store the value under
   * @param value - The value to store
   * @param expires - Optional expiration time in seconds, defaults to 1 week
   * @returns Promise that resolves when storage is complete
   */
  async setItem<T>(key: string, value: T, expires?: number): Promise<void> {
    return this.internalSetItem(key, value, expires ?? 604800);
  }

  /**
   * Stores a value with user-specific isolation.
   * Uses default expiration of 1 week (604800 seconds) if not specified.
   *
   * @template T - The type of value being stored
   * @param key - The key to store the value under
   * @param userId - The user ID to associate this data with
   * @param value - The value to store
   * @param expires - Optional expiration time in seconds, defaults to 1 week
   * @returns Promise that resolves when storage is complete
   */
  async setUserItem<T>(
    key: string,
    userId: string,
    value: T,
    expires?: number
  ): Promise<void> {
    return this.internalSetItem(key, value, expires ?? 604800, userId);
  }

  /**
   * Fetch and cache a JSON file by URL (or return cached value).
   * This method combines network fetching with automatic caching.
   *
   * Key features:
   * - Returns cached data if available and not expired
   * - Fetches fresh data when needed
   * - Automatically stores fetched data in the cache
   * - Supports forced refresh to bypass cache
   * - Maintains lastFetch reference for debugging
   *
   * @template T - The expected type of the response data
   * @param url - The URL to fetch data from
   * @param expiresSeconds - Optional cache expiration time in seconds, defaults to 1 week
   * @param userId - Optional user ID for user-specific caching
   * @param force - If true, forces a fresh fetch ignoring cache
   * @param init - Optional fetch configuration (headers, method, etc.)
   * @returns Promise resolving to the fetched data or null on failure
   */
  async fetch<T>(
    url: string,
    expiresSeconds?: number,
    userId?: string,
    force?: boolean,
    init?: RequestInit
  ): Promise<T | null> {
    const lastFetch = !force ? await this.getJsonData(url, userId) : null;
    try {
      if (!lastFetch) {
        const result = await this.getJsonFile(url, init);
        if (result == null) return null;
        await this.setUserItem(
          url,
          userId ?? emptyGuid,
          result,
          expiresSeconds ?? 604800
        );
        return result as T;
      }
      return lastFetch.data as T;
    } finally {
      this.lastFetch = lastFetch;
    }
  }

  /**
   * Executes and caches the result of an asynchronous action.
   * Similar to fetch but uses a callback function instead of a URL.
   *
   * This method is ideal for caching expensive operations like API calls,
   * database queries, or complex calculations.
   *
   * @template T - The expected type of the action result
   * @param itemName - A unique name for this cached item
   * @param action - Async function that produces the value to cache
   * @param expiresSeconds - Optional cache expiration time in seconds, defaults to 1 week
   * @param userId - Optional user ID for user-specific caching
   * @param force - If true, forces a fresh execution ignoring cache
   * @returns Promise resolving to the action result or null
   */
  async grab<T>(
    itemName: string,
    action: () => Promise<T | null>,
    expiresSeconds?: number,
    userId?: string,
    force?: boolean
  ): Promise<T | null> {
    const url = `grab://${itemName}`;
    const lastFetch = await this.getJsonData(url, userId);
    try {
      if (force || !lastFetch || this.jsonDataExpired(lastFetch)) {
        try {
          const result = await action();
          if (result == null) return null;
          await this.setUserItem(
            url,
            userId ?? emptyGuid,
            result,
            expiresSeconds ?? 604800
          );
          return result;
        } catch {
          return null;
        }
      }
      return lastFetch.data as T;
    } finally {
      this.lastFetch = lastFetch;
    }
  }

  /**
   * Returns both old (cached) and new (fresh) values.
   */
  async grabOldAndNew<T>(
    itemName: string,
    action: () => Promise<T | null>,
    expiresSeconds?: number,
    userId?: string,
    force?: boolean
  ): Promise<{ old: T | null; new: T | null }> {
    const url = `grab://${itemName}`;

    // Always get cached (even if expired) as "old"
    const cachedData = await this.getJsonData(url, userId, true);
    const oldValue = (cachedData?.data as T) ?? null;

    try {
      if (force || !cachedData || this.jsonDataExpired(cachedData)) {
        try {
          const newValue = await action();
          if (newValue !== null) {
            await this.setUserItem(
              url,
              userId ?? emptyGuid,
              newValue,
              expiresSeconds ?? 604800
            );
          }
          return { old: oldValue, new: newValue };
        } catch {
          return { old: oldValue, new: null };
        }
      }
      return { old: oldValue, new: oldValue };
    } finally {
      this.lastFetch = cachedData ?? null;
    }
  }

  _upgrades = [{ version: 1, execute: createVersion1 }];
  // return a list of upgrades that get this database to the correct version
  protected getDatabaseUpgrades(): db.DatabaseUpgrade[] {
    return this._upgrades;
  }

  private getId(key: string, userId: string = emptyGuid): string {
    return `${userId}:${key}`;
  }

  private expired(createDate: Date | string, expiresSeconds: number): boolean {
    const created = toDate(createDate);
    if (expiresSeconds === nextDayExpiry) {
      const today = new Date();
      return !sameDay(today, created);
    }
    const now = Date.now();
    return created.getTime() + (expiresSeconds ?? 0) * 1000 < now;
  }

  private async internalSetItem<T>(
    key: string,
    value: T,
    expires: number = 604800,
    userId: string = emptyGuid
  ): Promise<void> {
    const item: JsonData = {
      id: this.getId(key, userId),
      data: value,
      created: new Date(),
      expires: expires,
    };
    await super.put(item, stores.all);
  }

  private async getJsonFile(
    path: string,
    init?: RequestInit
  ): Promise<unknown | null> {
    try {
      const response = await fetch(path, init);
      if (response.ok) {
        // Guard: Some endpoints return empty bodies with 204/200
        const text = await response.text();
        if (!text) return null;
        return JSON.parse(text);
      }
      return null;
    } catch {
      return null;
    }
  }

  // Force close all connections immediately
  public shutdown(): void {
    this.forceClose();
  }
}

declare global {
  var noSqlDb: NoSqlDb | undefined;
}

globalThis.noSqlDb = globalThis.noSqlDb || new NoSqlDb();

export const noSqlDb = globalThis.noSqlDb;

export function getNoSqlDb(): NoSqlDb {
  return globalThis.noSqlDb as NoSqlDb;
}

// Add event listener for page unload to ensure connections are closed
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (globalThis.noSqlDb) {
      globalThis.noSqlDb.shutdown();
    }
  });
}
